import axios from 'axios';
import { apiClient } from './api';

const STORAGE_KEY = 'tark_pending_account_recovery_v1';
const RECOVERY_TTL_MS = 30 * 60 * 1000;

interface PendingAccountRecovery {
  phoneNumber: string;
  createdAt: number;
}

export interface WebPhoneChangeResponse {
  success: boolean;
  message?: string;
  newPhoneNumber?: string;
  expiresIn?: number;
  error?: string;
  requiresReauthentication?: boolean;
}

export function normalizeUsPhone(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

export function savePendingAccountRecovery(phoneNumber: string): boolean {
  const normalized = normalizeUsPhone(phoneNumber);
  if (!normalized || typeof window === 'undefined') return false;

  try {
    const value: PendingAccountRecovery = { phoneNumber: normalized, createdAt: Date.now() };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function getPendingAccountRecovery(): PendingAccountRecovery | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PendingAccountRecovery>;
    const normalized = typeof value.phoneNumber === 'string' ? normalizeUsPhone(value.phoneNumber) : null;
    if (!normalized || typeof value.createdAt !== 'number' || Date.now() - value.createdAt > RECOVERY_TTL_MS) {
      clearPendingAccountRecovery();
      return null;
    }
    return { phoneNumber: normalized, createdAt: value.createdAt };
  } catch {
    clearPendingAccountRecovery();
    return null;
  }
}

export function clearPendingAccountRecovery(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Browser storage may be unavailable in strict privacy modes.
  }
}

export function isEmailAlreadyExistsError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const data = error.response?.data as { error?: string; code?: string } | undefined;
  return data?.error === 'email_already_exists' || data?.code === 'email_already_exists';
}

export function getIdentityRecoveryError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    const message = data?.error || data?.message;
    if (message?.trim()) return message;
  }
  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

export async function requestWebPhoneChange(newPhoneNumber: string): Promise<WebPhoneChangeResponse> {
  const response = await apiClient.post<WebPhoneChangeResponse>(
    '/api/v1/web/user/changes/change-phone/request',
    { newPhoneNumber }
  );
  return response.data;
}

export async function verifyWebPhoneChange(
  newPhoneNumber: string,
  verificationCode: string
): Promise<WebPhoneChangeResponse> {
  const response = await apiClient.post<WebPhoneChangeResponse>(
    '/api/v1/web/user/changes/change-phone/verify',
    { newPhoneNumber, verificationCode }
  );
  return response.data;
}